import { z } from "zod";
import { CALIBRATION_DEFAULTS } from './calibration';

/**
 * Core Identity & Data Protocol Schemas
 * These schemas serve as the Single Source of Truth (SSOT) for the CartLens application.
 * They are used for validation, type generation, and AI tool definitions.
 */

export const CategoryInfoSchema = z.object({
  name: z.string().describe("The display name of the category"),
  color: z.string().describe("Hex color code used for the category UI elements"),
  emoji: z.string().optional().nullable().describe("Emoji icon representing the category"),
  translationKey: z.string().optional().nullable().describe("i18n key for localized category names"),
  parsingRules: z.string().optional().nullable().describe("AI instructions for how to parse items in this category"),
});

export const StoreInfoSchema = z.object({
  name: z.string().describe("The name of the store or merchant"),
  address: z.string().optional().nullable().describe("Physical address of the store"),
  url: z.string().optional().nullable().describe("Website or Google URL for the store"),
  id: z.string().optional().nullable().describe("Internal or external store identifier"),
  foursquare_id: z.string().optional().nullable().describe("Foursquare venue ID for geocoding"),
  coordinates: z.object({
    lat: z.number().describe("Latitude coordinate"),
    lng: z.number().describe("Longitude coordinate"),
  }).optional().nullable().describe("GPS coordinates of the store location"),
  is_verified: z.boolean().optional().nullable().describe("Whether the store is an approved Lattice Partner"),
  source: z.enum(['discovery', 'history']).optional().nullable().describe("Where a store-picker entry came from: live discovery, or the user's past trips (fallback when discovery has nothing)"),
});

const UnitPriceBasisSchema = z.enum(['sale', 'regular', 'member']);
const ReadConfidenceSchema = z.enum(['high', 'medium', 'low']);

/**
 * What a shelf tag (or a weighed receipt line) printed, saved on a basket item
 * as metadata.unit_price_check. The check itself (match or mismatch, CartLens's
 * own unit price) is worked out from the item's current price and size wherever
 * it's shown: compareUnitPrice in intelligence.ts.
 */
export const ShelfUnitPriceSchema = z.object({
  source: z.enum(['tag', 'receipt']).describe("A shelf tag, or a weighed receipt line"),
  shelfUnitPrice: z.number().describe("The unit price as printed (per shelfPer shelfUnit)"),
  shelfPer: z.number().describe("How much the printed unit price is per (1 for per oz, 100 for per 100 g)"),
  shelfUnit: z.string().describe("The printed unit-price unit (oz, lb, g, ct, fl oz…)"),
  basis: UnitPriceBasisSchema.nullable().optional().describe("Which tag price the unit price belongs to"),
  usesItemPrice: z.boolean().optional().describe("False when the unit price belongs to another tag price (e.g. the regular price beside a sale price)"),
  otherPrice: z.number().optional().describe("That other tag price, when usesItemPrice is false"),
  readSize: z.number().optional().describe("The size as read from the image"),
  readSizeUnit: z.string().optional().describe("Its unit as read"),
  sizeTrusted: z.boolean().optional().describe("False when the reader wasn't sure of the size: no check until the shopper changes it"),
  // Saved by builds before 2026-10-24, which stored a snapshot of the whole check.
  price: z.number().optional().describe("Legacy: the price the snapshot compared"),
  size: z.number().optional().describe("Legacy: the size the snapshot compared"),
  sizeUnit: z.string().optional().describe("Legacy: that size's unit"),
  dismissed: z.boolean().optional().describe("Legacy: the shopper tapped Looks right on that snapshot"),
});

export const ShoppingItemSchema = z.object({
  id: z.string().describe("Unique identifier for the item"),
  name: z.string().describe("The formatted name of the product following the nomenclature protocol"),
  brand: z.string().optional().nullable().describe("Extracted brand name"),
  product: z.string().optional().nullable().describe("Extracted base product name"),
  variety: z.string().optional().nullable().describe("Extracted variety or descriptors"),
  size: z.union([z.number(), z.string()]).optional().nullable().describe("Extracted numeric or string size (e.g. 12 or '12oz')"),
  price: z.number().describe("The numeric price of the item"),
  quantity: z.number().describe("The number of units being purchased"),
  category: z.string().describe("The name of the category this item belongs to"),
  imageUrl: z.string().optional().nullable().describe("URL to an image of the item or its receipt fragment"),
  unit: z.string().optional().nullable().describe("The measurement unit (e.g. oz, lb, kg, ct)"),
  isPurchased: z.boolean().optional().nullable().describe("Whether the item has been marked as bought"),
  color: z.string().optional().nullable().describe("Hex color for visual grouping"),
  canonicalId: z.string().optional().nullable().describe("A semantic, English-only identifier for price tracking (slug format)"),
  embedding: z.array(z.number()).optional().nullable().describe("Vector embedding for the item (768 dimensions)"),
  priceStatus: z.enum(["up", "down", "stable"]).optional().nullable().describe("Trend indicator relative to historical price"),
  usualPrice: z.number().optional().nullable().describe("The calculated normal price for this item at this store"),
  shelfLife: z.number().optional().nullable().describe("Estimated shelf life in days"),
  expirationDate: z.string().optional().nullable().describe("Calculated or manual expiration date"),
  latticeRecord: z.number().optional().nullable().describe("The best known price in the lattice (the 'World Record') for this context"),
  latticeReports: z.number().optional().nullable().describe("Distinct shoppers behind the latticeRecord at its store; a Lattice-Beater needs at least MIN_LATTICE_REPORTS"),
  upc: z.string().optional().nullable().describe("Verified 12-digit Universal Product Code"),
  gtin: z.string().optional().nullable().describe("Verified 14-digit Global Trade Item Number"),
  plu_code: z.string().optional().nullable().describe("Verified Price Look-Up code for produce"),
  metadata: z.record(z.string(), z.any()).optional().nullable().describe("Additional item intelligence metadata"),
  betterDeal: z.object({
    price: z.number(),
    storeName: z.string(),
    address: z.string().optional().nullable(),
    distance: z.number().optional().nullable(),
    latitude: z.number().optional().nullable(),
    longitude: z.number().optional().nullable(),
    date: z.string().optional().nullable(),
    reports: z.number().optional().nullable().describe("Distinct shoppers who reported that price at that store within the Horizon"),
  }).optional().nullable().describe("Information about a better price found in the global lattice"),
  priceCheck: z.object({
    usualPrice: z.number(),
  }).optional().nullable().describe("Set when the price is over 10x off what the exact product usually costs nearby (likely a misread)"),
});

export const ShoppingHistoryEntrySchema = z.object({
  id: z.string().describe("Unique identifier for the history record"),
  date: z.string().describe("ISO timestamp of when the shopping trip occurred"),
  archivedAt: z.string().optional().nullable().describe("ISO timestamp of when the trip was archived"),
  items: z.array(ShoppingItemSchema).describe("List of items purchased during this trip"),
  totalCost: z.number().describe("The total numeric cost of all items in the trip"),
  total: z.number().optional().nullable().describe("Alias for totalCost"),
  leakageAmount: z.number().optional().nullable().describe("Amount of economic leakage or profit for this trip"),
  leakage_amount: z.number().optional().nullable().describe("Snake_case alias for leakageAmount"),
  savingsAmount: z.number().optional().nullable().describe("Amount of economic savings/wins for this trip"),
  savings_amount: z.number().optional().nullable().describe("Snake_case alias for savingsAmount"),
  budgetAmount: z.number().optional().nullable().describe("Budget the user set for this trip; null when none was set"),
  storeInfo: StoreInfoSchema.optional().nullable().describe("Details about where the trip occurred"),
  store: StoreInfoSchema.optional().nullable().describe("Alias for storeInfo"),
  receiptImageUrl: z.string().optional().nullable().describe("URL to the full receipt image"),
  userId: z.string().optional().nullable().describe("The user this record belongs to"),
  isLatticeContribution: z.boolean().optional().nullable().describe("Whether this trip's prices are shared with the Geospatial Lattice"),
  isPersonalTrip: z.boolean().optional().nullable().describe("Whether this trip counts toward the user's own tracked spend, vs. a donated (not-personal) Lattice contribution"),
});

export const ExtractionResultSchema = z.object({
  isReceipt: z.boolean().describe("Whether the image is a commercial receipt (true) or just product items/tags (false)"),
  storeName: z.string().optional().nullable().describe("AI-identified store name from receipt pixels"),
  storeAddress: z.string().optional().nullable().describe("AI-identified store address from receipt pixels"),
  storeAddressNormalized: z.string().optional().nullable().describe("The store address as a clean postal address (Street, City, State ZIP), for geocoding"),
  date: z.string().optional().nullable().describe("AI-identified date in YYYY-MM-DD format"),
  time: z.string().optional().nullable().describe("AI-identified time in HH:mm format"),
  totalAmount: z.number().optional().nullable().describe("The grand total shown on the receipt (including taxes/fees)"),
  subtotal: z.number().optional().nullable().describe("The subtotal shown on the receipt (before taxes/fees)"),
  items: z.array(z.object({
    name: z.string().optional().nullable().describe("Grounded display name assigned during identity resolution"),
    brand: z.string().optional().nullable().describe("Extracted brand name (e.g. Heinz)"),
    product: z.string().describe("Base product name (e.g. Ketchup)"),
    variety: z.string().optional().nullable().describe("Variety or descriptors (e.g. Organic, Spicy)"),
    size: z.union([z.number(), z.string()]).optional().nullable().describe("Numeric or string size value (e.g. 12 or '12oz')"),
    unit: z.string().optional().nullable().describe("Measurement unit (e.g. oz, lb, g)"),
    price: z.number().describe("Numeric price per item/unit"),
    category: z.string().describe("Suggested category for the item"),
    quantity: z.number().optional().nullable().describe("Number of items purchased (default 1)"),
    canonicalId: z.string().optional().nullable().describe("The generated protocol slug for this item"),
    embedding: z.array(z.number()).optional().nullable().describe("Vector embedding for the item"),
    shelfLife: z.number().optional().nullable().describe("Estimated shelf life in days"),
    family: z.string().optional().nullable().describe("Generic shopping-list name in English (\"eggs\", \"printer ink\"); brands and sizes of the same thing share it. Saved as metadata.family"),
    replenishable: z.boolean().optional().nullable().describe("Bought again and again as it's used up (true), a one-off or long-lasting purchase (false); saved as metadata.replenishable"),
    upc: z.string().optional().nullable(),
    gtin: z.string().optional().nullable(),
    plu_code: z.string().optional().nullable(),
    metadata: z.record(z.string(), z.any()).optional().nullable(),
    receiptText: z.string().optional().nullable(),
    // Read for the shelf unit-price check.
    shelfUnitPrice: z.number().optional().nullable().describe("Unit price as printed on the tag (or a weighed receipt line's rate)"),
    shelfUnitPriceUnit: z.string().optional().nullable().describe("Its unit as printed (oz, lb, 100 g, 100 ct…)"),
    shelfUnitPriceBasis: UnitPriceBasisSchema.optional().nullable().describe("Which tag price the unit price belongs to"),
    priceBasis: UnitPriceBasisSchema.optional().nullable().describe("Which tag price 'price' is"),
    regularPrice: z.number().optional().nullable().describe("The regular price, when a sale or member price is also shown"),
    memberPrice: z.number().optional().nullable().describe("A member/loyalty-card price"),
    isMultiBuy: z.boolean().optional().nullable().describe("The price is a multi-buy deal (2 for $5)"),
    sizeConfidence: ReadConfidenceSchema.optional().nullable(),
    unitPriceConfidence: ReadConfidenceSchema.optional().nullable(),
  })).describe("List of items extracted from the image"),
});

export const ProductCanonicalSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  slug: z.string(),
  default_category: z.string().optional().nullable(),
  shelf_life_days: z.number().default(30),
  is_ignored: z.boolean().default(false),
  created_at: z.string().optional(),
});

export const InsightDataSchema = z.object({
  title: z.string().describe("The high-level topic of the AI insight"),
  observation: z.string().describe("The AI's specific finding from the data analysis"),
  realtime_tip: z.string().optional().nullable().describe("A proactive, immediate suggestion for the user"),
  historical_tips: z.array(z.string()).describe("Long-term saving or optimization strategies"),
  confidence_score: z.number().optional().nullable().describe("Metric indicating AI's certainty in this insight (0-1)"),
  generatedAt: z.string().describe("Timestamp of when the insight was produced"),
});

export const BasketTipSchema = z.object({
  type: z.enum(["duplicate", "suggestion", "saving", "persona", "substitution"]).describe("The category of the tip"),
  message: z.string().describe("The actual text content of the suggestion"),
  relatedItem: z.string().optional().nullable().describe("The name of the item this tip refers to"),
  substitution: z.object({
    original: z.string().describe("The name of the original item"),
    alternative: z.string().describe("The name of the suggested lower-cost alternative"),
    savings: z.number().describe("The estimated numeric savings amount"),
  }).optional().nullable().describe("Detailed substitution data for the Alpha Engine"),
});

export const AiPersonaSchema = z.enum(["frugal", "premium", "analyst", "efficiency"]).describe("The behavioral logic style of the AI sidekick");

export const AiProfileBaseSchema = z.object({
  assistant: z.object({
    name: z.string().optional().nullable().describe("The display name of the AI assistant"),
    persona: AiPersonaSchema.optional().nullable().describe("The active persona determining the AI's tone and logic"),
  }).optional().nullable().describe("Configuration for the AI assistant identity"),
  global_bio: z.string().optional().nullable().describe("Broad context about the user's life and shopping habits"),
  last_insight_at: z.string().optional().nullable().describe("Timestamp of last master insight generation"),
  loyalty_programs: z.array(z.any()).optional().nullable().describe("List of merchants where the user has active memberships"),
  metadata: z.object({
    model_preference: z.enum(["flash", "pro"]).optional().nullable().describe("User's preferred Gemini model tier for this profile"),
  }).optional().nullable().describe("Additional AI metadata"),
  domains: z.object({
    grocery: z.object({
      dietary: z.array(z.string()).optional().nullable().describe("Food restrictions or choices (e.g. Gluten-Free)"),
      allergies: z.array(z.string()).optional().nullable().describe("Specific allergens to watch for in ingredients"),
      organic_pref: z.boolean().optional().nullable().describe("Whether to prioritize organic options in suggestions"),
      pantry_staples: z.array(z.string()).optional().nullable().describe("Core items always needed in the user's household"),
    }).optional().nullable().describe("Grocery-specific knowledge"),
    apparel: z.object({
      sizes: z.object({
        top: z.string().optional().nullable().describe("User's standard top/shirt size"),
        waist: z.number().optional().nullable().describe("User's waist measurement"),
        shoe: z.string().optional().nullable().describe("User's shoe size with regional indicator if possible"),
      }).optional().nullable().describe("User's clothing size profile"),
      color_palette: z.array(z.string()).optional().nullable().describe("Preferred colors for fashion"),
      exclusions: z.array(z.string()).optional().nullable().describe("Materials or styles the user avoids"),
    }).optional().nullable().describe("Fashion and apparel context"),
    electronics: z.object({
      subscriptions: z.string().optional().nullable().describe("Technology subscriptions entered in Settings"),
      ecosystems: z.array(z.string()).optional().nullable().describe("Preferred tech brands/ecosystems (e.g. Apple, Android)"),
      tech_level: z.enum(["pro", "casual"]).optional().nullable().describe("The user's technical proficiency level"),
      warranty_priority: z.boolean().optional().nullable().describe("Whether protection plans are a priority for the user"),
    }).optional().nullable().describe("Technology and gadget preferences"),
    automotive: z.object({
      vehicles: z.array(z.object({
        nickname: z.string().optional().nullable().describe("The user's name for this vehicle"),
        type: z.enum([
          "car", "motorcycle", "bicycle", "truck", "suv", "van", "scooter", "boat", "rv", "trailer", "other"
        ]).optional().nullable().describe("The category of transport"),
        make: z.string().optional().nullable().describe("Manufacturer (e.g. Toyota)"),
        model: z.string().optional().nullable().describe("Specific model name (e.g. Camry)"),
        year: z.number().optional().nullable().describe("The manufacturing year"),
        fuel: z.enum(["gas", "diesel", "electric", "hybrid", "none"]).optional().nullable().describe("The power source type"),
        gas_type: z.enum(["regular", "mid-grade", "premium", "diesel"]).optional().nullable().describe("Preferred octance or fuel grade"),
        insurance_provider: z.string().optional().nullable().describe("Current insurance carrier"),
      })).optional().nullable().describe("The user's fleet of vehicles"),
      diy_level: z.enum(["none", "basic", "pro"]).optional().nullable().describe("How much maintenance the user performs themselves"),
    }).optional().nullable().describe("Vehicle and transportation context"),
    home_living: z.object({
      residency: z.enum(["Owned", "Rented", "Shared"]).optional().nullable().describe("The user's legal relationship with their primary residence"),
      space_type: z.enum(["Apartment", "House", "Studio"]).optional().nullable().describe("The physical category of the residence"),
      style: z.enum(["modern", "rustic", "minimalist"]).optional().nullable().describe("Preferred interior design aesthetic"),
      rooms: z.number().optional().nullable().describe("The number of rooms in the user's primary residence"),
      garden: z.boolean().optional().nullable().describe("Whether the residence has a garden or outdoor space"),
      tool_system: z.string().optional().nullable().describe("Preferred battery or tool platform (e.g. Milwaukee M18)"),
    }).optional().nullable().describe("Home maintenance and decor context"),
    dependents: z.object({
      species: z.array(z.string()).optional().nullable().describe("Types of pets in the household"),
      kids: z.array(z.object({
        birth_year: z.number().optional().nullable().describe("Birth year of the child"),
        needs: z.string().optional().nullable().describe("Specific needs or interests for this child"),
      })).optional().nullable().describe("List of children in the household"),
      specific_needs: z.string().optional().nullable().describe("General household dependency context"),
    }).optional().nullable().describe("Pets and children context"),
    hobbies: z.object({
      interests: z.array(z.string()).optional().nullable().describe("What the user does for leisure"),
      learning_goals: z.array(z.string()).optional().nullable().describe("Skills or topics the user is currently studying"),
    }).optional().nullable().describe("Leisure and self-improvement context"),
    health: z.object({
      allergies: z.array(z.string()).optional().nullable().describe("Medical or skin-contact allergies"),
      medications: z.array(z.string()).optional().nullable().describe("Regular prescriptions or supplements"),
      fitness_level: z.enum(["sedentary", "active", "athlete"]).optional().nullable().describe("Daily physical activity level"),
      skin_type: z.string().optional().nullable().describe("Skin profile for care products (e.g. Oily, Sensitive)"),
    }).optional().nullable().describe("Health and wellness context"),
  }).optional().nullable().describe("Domain-specific intelligence trees for hyper-personalized AI analysis"),
});

export const AiProfileSchema = AiProfileBaseSchema.optional().nullable().describe("The full identity structure used for AI reasoning and UI personalization");

export const UserProfileSchema = z.object({
  id: z.string().describe("The user's unique identification from Clerk/Supabase"),
  email: z.string().optional().nullable().describe("The primary contact email"),
  first_name: z.string().optional().nullable().describe("User's legal or preferred first name"),
  last_name: z.string().optional().nullable().describe("User's family name"),
  avatar_url: z.string().optional().nullable().describe("URL to the user's profile picture"),
  role: z.enum(['consumer', 'architect', 'store_partner', 'admin']).default('consumer').describe("The operational role of the user within the ecosystem"),
  ai_profile: AiProfileSchema.optional().nullable().describe("The core Spending DNA structure"),
  settings: z.object({
    interface: z.object({
      language: z.string().default("en").describe("Preferred two-letter language code (e.g. en)"),
      currency: z.string().default("USD").describe("Preferred three-letter currency code (e.g. USD)"),
    }).default({ language: "en", currency: "USD" }),
    intelligence: z.object({
      lattice_horizon_hours: z.number().default(CALIBRATION_DEFAULTS.latticeHorizonHours).describe("Temporal lookback for price discovery"),
      lattice_radius_miles: z.number().default(CALIBRATION_DEFAULTS.latticeRadiusMiles).describe("Geospatial range for price discovery"),
      value_of_time_per_hour: z.number().default(CALIBRATION_DEFAULTS.valueOfTimePerHour).describe("User's self-assessed dollar value of one hour, used by Basket IQ's route cost model to weigh money saved against detour time"),
    }).default({ lattice_horizon_hours: CALIBRATION_DEFAULTS.latticeHorizonHours, lattice_radius_miles: CALIBRATION_DEFAULTS.latticeRadiusMiles, value_of_time_per_hour: CALIBRATION_DEFAULTS.valueOfTimePerHour }),
    privacy: z.object({
      lattice_participation: z.boolean().default(true).describe("Whether ordinary personal trips also share their price data with the Geospatial Lattice"),
      cookie_consent: z.object({
        analytics: z.boolean(),
        advertising: z.boolean(),
        decided_at: z.string(),
        version: z.number(),
      }).optional().nullable().describe("Cookie consent choice from the banner: Analytics and Advertising categories (Necessary is always on)"),
    }).default({ lattice_participation: true }),
    onboarding: z.object({
      walkthrough: z.object({
        version: z.number(),
        status: z.enum(['completed', 'skipped']),
        watched_chapters: z.array(z.string()),
        finished_at: z.string(),
      }).optional(),
    }).optional().describe("Animated welcome walkthrough outcome; preview viewing is distinct from a real scan"),
    restock: z.object({
      hidden: z.record(z.string(), z.boolean().nullable()).optional().describe("Restock Radar items the user said not to suggest again, by item key"),
      snoozed: z.record(z.string(), z.string().nullable()).optional().describe("Snoozed Restock Radar items: item key -> ISO time the snooze ends"),
    }).optional().describe("Restock Radar and Suggested Essentials preferences"),
    location: z.object({
      zip_code: z.string().optional().nullable().describe("Primary ZIP code for store detection"),
    }).optional().describe("Location preferences (replaces the profiles.zip_code column)"),
    budget: z.object({
      prompt_on_store_select: z.boolean().default(true).describe("Trip Budget: ask for a budget after the user picks a store (and before a capture action when none is set)"),
    }).optional().describe("Trip Budget preferences"),
  }).default({
    interface: { language: "en", currency: "USD" },
    intelligence: { lattice_horizon_hours: CALIBRATION_DEFAULTS.latticeHorizonHours, lattice_radius_miles: CALIBRATION_DEFAULTS.latticeRadiusMiles, value_of_time_per_hour: CALIBRATION_DEFAULTS.valueOfTimePerHour },
    privacy: { lattice_participation: true }
  }).describe("Unified Sovereign Settings Protocol"),
  last_history_change: z.string().optional().nullable().describe("Timestamp of last significant history update"),
  is_pro: z.boolean().optional().nullable().describe("Subscription status indicator for the Pro tier"),
  is_ultra: z.boolean().optional().nullable().describe("Subscription status indicator for the Ultra tier"),
  is_architect: z.boolean().optional().nullable().describe("Whether the user has authorized architect status"),
  is_partner: z.boolean().optional().nullable().describe("Whether the user has authorized merchant partner status"),
  store_id: z.string().optional().nullable().describe("The specific store managed by this partner"),
  zip_code: z.string().optional().nullable().describe("Legacy column; the app reads settings.location.zip_code first and no longer writes this"),
  subscriptionInterval: z.enum(["month", "year"]).optional().nullable().describe("The active billing interval from Clerk"),
  subscription_interval: z.enum(["month", "year"]).optional().nullable().describe("Snake_case alias for subscriptionInterval"),
  subscription_ends_at: z.string().optional().nullable().describe("Timestamp when the active subscription ends"),
  trial_ends_at: z.string().optional().nullable().describe("Timestamp when the free trial ends"),
  cancel_at_period_end: z.boolean().optional().nullable().describe("Whether the subscription is scheduled for cancellation"),
  credits: z.number().optional().nullable().describe("Available Ultra Credits for computed intelligence"),
  has_onboarded: z.boolean().optional().nullable().describe("Whether the user has completed the visual setup"),
  last_insight_at: z.string().optional().nullable().describe("Timestamp of last master insight generation"),
  storage_used_bytes: z.number().optional().nullable().describe("Total cloud vault usage"),
  extra_storage_bytes: z.number().optional().nullable().describe("Additional purchased storage capacity"),
  metadata: z.any().optional().nullable().describe("JSON blob for non-standardized user settings"),
  updated_at: z.string().optional().nullable().describe("Last profile update timestamp"),
  loyalty_programs: z.array(z.object({
    merchant: z.string().describe("The name of the retailer"),
    memberId: z.string().optional().nullable().describe("Membership or account number"),
    default_discount: z.number().optional().nullable().describe("Automatic percentage discount to apply"),
    notes: z.string().optional().nullable().describe("Usage tips or redemption info"),
  })).optional().nullable().describe("Legacy column; loyalty programs now live in ai_profile.loyalty_programs"),
}).describe("The top-level Single Source of Truth for a CartLens user profile");

export const LanguageSchema = z.object({
  code: z.string().describe("ISO 639-1 language code"),
  name: z.string().describe("The display name of the language"),
  flag: z.string().describe("Emoji flag representing the language region"),
  defaultCurrency: z.string().optional().nullable().describe("The primary currency used in that region"),
});

export const CurrencySchema = z.object({
  code: z.string().describe("Three-letter currency code (e.g. USD)"),
  label: z.string().describe("Full name of the currency"),
  symbol: z.string().describe("Symbol used for display (e.g. $)"),
  flag: z.string().describe("Emoji flag for the currency region"),
  country: z.string().describe("Primary country using this currency"),
  countryKey: z.string().optional().nullable().describe("Localized country name key"),
});

export const ArchitectApplicationSchema = z.object({
  id: z.string().optional(),
  user_id: z.string(),
  full_name: z.string(),
  mobile: z.string(),
  zip_code: z.string(),
  radius: z.string(),
  device_model: z.string(),
  frequency: z.string(),
  retailers: z.array(z.string()).default([]),
  motivation: z.string(),
  social_proof: z.string().optional().nullable(),
  status: z.enum(['pending', 'reviewing', 'approved', 'rejected']).default('pending'),
  created_at: z.string().optional(),
});

export const MerchantPartnerApplicationSchema = z.object({
  id: z.string().optional(),
  user_id: z.string(),
  business_name: z.string(),
  tax_id: z.string(),
  category: z.string(),
  operational_scale: z.number(),
  address: z.string(),
  foot_traffic: z.string(),
  data_strategy: z.string(),
  contact_first: z.string(),
  contact_last: z.string(),
  contact_email: z.string(),
  contact_phone: z.string(),
  goal: z.string(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  store_url: z.string().optional().nullable(),
  status: z.enum(['pending', 'reviewing', 'approved', 'rejected']).default('pending'),
  created_at: z.string().optional(),
});

// Type Definitions inferred from Schemas
export type CategoryInfo = z.infer<typeof CategoryInfoSchema>;
export type StoreInfo = z.infer<typeof StoreInfoSchema>;
export type ShoppingItem = z.infer<typeof ShoppingItemSchema>;
export type ShoppingHistoryEntry = z.infer<typeof ShoppingHistoryEntrySchema>;
export type ExtractionResult = z.infer<typeof ExtractionResultSchema>;
export type ShelfUnitPrice = z.infer<typeof ShelfUnitPriceSchema>;
export type InsightData = z.infer<typeof InsightDataSchema>;
export type BasketTip = z.infer<typeof BasketTipSchema>;
export type AiPersona = z.infer<typeof AiPersonaSchema>;
export type AiProfile = z.infer<typeof AiProfileSchema>;
export type UserProfile = z.infer<typeof UserProfileSchema>;
export type Language = z.infer<typeof LanguageSchema>;
export type Currency = z.infer<typeof CurrencySchema>;
export type ArchitectApplication = z.infer<typeof ArchitectApplicationSchema>;
export type MerchantPartnerApplication = z.infer<typeof MerchantPartnerApplicationSchema>;

/**
 * mapFlatToDeep - Recursive utility to resolve where a flat key belongs in a nested Zod schema.
 * This enables the AI to send flat updates like { dietary: [...] } which are automatically
 * routed to { ai_profile: { domains: { grocery: { dietary: [...] } } } }.
 */
export const mapFlatToDeep = (updates: Record<string, any>, schema: z.ZodObject<any>): Record<string, any> => {
  const result: Record<string, any> = {};
  const schemaShape = schema.shape;

  const findPath = (targetKey: string, currentShape: any, currentPath: string[] = []): string[] | null => {
    // 1. Check if key is at this level
    if (currentShape[targetKey]) {
      return [...currentPath, targetKey];
    }

    // 2. Recurse into nested objects
    for (const [key, zodType] of Object.entries(currentShape)) {
      let innerType = zodType as any;
      // Handle optionals/nullables
      while (innerType._def.innerType || innerType._def.schema) {
        innerType = innerType._def.innerType || innerType._def.schema;
      }

      if (innerType instanceof z.ZodObject) {
        const found = findPath(targetKey, innerType.shape, [...currentPath, key]);
        if (found) return found;
      }
    }
    return null;
  };

  Object.entries(updates).forEach(([key, value]) => {
    // Special Alias Mappings for easier AI updates
    if (key === 'assistant_name') {
      result.ai_profile = result.ai_profile || {};
      result.ai_profile.assistant = result.ai_profile.assistant || {};
      result.ai_profile.assistant.name = value;
      return;
    }

    // Unified Settings Protocol Mappings
    if (key === 'language' || key === 'currency') {
      result.settings = result.settings || {};
      result.settings.interface = result.settings.interface || {};
      result.settings.interface[key] = value;
      return;
    }

    // Former flat columns (see migration 20261024160000)
    if (key === 'zip_code' || key === 'zip') {
      result.settings = result.settings || {};
      result.settings.location = { ...(result.settings.location || {}), zip_code: value };
      return;
    }
    if (key === 'loyalty_programs') {
      result.ai_profile = { ...(result.ai_profile || {}), loyalty_programs: value };
      return;
    }

    if (key === 'radius' || key === 'lattice_radius_miles') {
      result.settings = result.settings || {};
      result.settings.intelligence = result.settings.intelligence || {};
      result.settings.intelligence.lattice_radius_miles = value;
      return;
    }

    if (key === 'horizon' || key === 'lattice_horizon_hours') {
      result.settings = result.settings || {};
      result.settings.intelligence = result.settings.intelligence || {};
      result.settings.intelligence.lattice_horizon_hours = value;
      return;
    }

    const path = findPath(key, schemaShape);
    if (path) {
      // Build nested result object
      let current = result;
      for (let i = 0; i < path.length; i++) {
        const part = path[i];
        if (i === path.length - 1) {
          current[part] = value;
        } else {
          current[part] = current[part] || {};
          current = current[part];
        }
      }
    } else {
      // If not found in schema, keep at root (might be a new/untracked field)
      result[key] = value;
    }
  });

  return result;
};
